import { todayKey } from './week'

/**
 * Where the timetable is, worked out from the address and nothing else.
 *
 * The address is always `<view>/<day>` and then, optionally, what is open on top of it:
 * `#/timetable/week/2026-08-17`, `#/timetable/day/2026-08-17/<appointmentId>`,
 * `#/timetable/day/2026-08-17/<appointmentId>/session`, `#/timetable/week/2026-08-17/new`.
 *
 * **The week is part of the address even when an appointment is open**, and that is the point of the shape
 * rather than a detail of it: opening a lecture in November must not send the plan back to today
 * when it is closed again, and a link somebody sends has to arrive on the week they were looking at.
 *
 * **Which half of an appointment is open is in the address too.** An appointment has two tabs — what is
 * on, and what came of it — and a link that dropped which one was being read would send a search hit
 * on somebody's own write-up to a room number. The plain address is the appointment; `…/session` is
 * the other half, so the back button walks between them as well.
 */

export const TIMETABLE_AREA = 'timetable'

export type View = 'week' | 'day'

/** The three things that can stand open over the plan, beside an appointment. */
const NEW = 'new'
const CALENDARS = 'calendars'

/** The one step that says which half of an appointment is being read. */
const SESSION = 'session'

/** Stands in the day's position where the day is not known yet. Never reads as a day. */
const SOMEWHEN = '-'

/** Which half of an open appointment is being read. */
export type Tab = 'appointment' | 'session'

export interface Opened {
  readonly view: View
  /** The day the view is anchored on, as `YYYY-MM-DD`. For a week: any day inside it. */
  readonly day: string
  /** The appointment that is open, or `null`. */
  readonly appointmentId: string | null
  /** Which half of it is open. The appointment itself unless the address says otherwise. */
  readonly tab: Tab
  /** Whether the sheet for a new appointment is open. */
  readonly writing: boolean
  /** Whether the list of subscribed calendars is open. */
  readonly subscribing: boolean
}

export function openedIn(path: readonly string[], today: string = todayKey()): Opened {
  const [first, second, third, fourth] = path
  const view: View = first === 'day' ? 'day' : 'week'
  const day = isDay(second) ? second : today

  return {
    view,
    day,
    appointmentId: third !== undefined && ![NEW, CALENDARS].includes(third) ? third : null,
    tab: fourth === SESSION ? 'session' : 'appointment',
    writing: third === NEW,
    subscribing: third === CALENDARS,
  }
}

/** The address of a week. The day inside it is kept as it was, so "week" and "day" toggle cleanly. */
export function pathToWeek(day: string): string[] {
  return ['week', day]
}

export function pathToDay(day: string): string[] {
  return ['day', day]
}

/**
 * One appointment, opened over the view somebody is standing in.
 *
 * The plain address is what is on; `…/session` is what came of it. A search hit on a write-up uses
 * the second, so that finding your own sentence again lands you where that sentence is.
 */
export function pathToAppointment(
  view: View,
  day: string,
  appointmentId: string,
  tab: Tab = 'appointment',
): string[] {
  return tab === 'session' ? [view, day, appointmentId, SESSION] : [view, day, appointmentId]
}

/**
 * One appointment to whoever does not know which week it is in.
 *
 * A link or a subject knows a kind and an id and deliberately nothing else — that genericity is the
 * whole point of them — so neither can name the day. The day is not optional in this address, though:
 * `#/timetable/<id>` reads as a *view*, and an unknown view is this week with nothing open on it,
 * which is exactly the wrong answer to "open this lecture".
 *
 * So the day says "whenever this is", and the timetable corrects the address to the week the
 * appointment is actually in the moment it has asked — one request, made only when arriving this way.
 */
export function pathToAppointmentWheneverItIs(appointmentId: string): string[] {
  return ['week', SOMEWHEN, appointmentId]
}

/** One of the three sheets, opened over the view somebody is standing in. */
export function pathToNew(view: View, day: string): string[] {
  return [view, day, NEW]
}

export function pathToCalendars(view: View, day: string): string[] {
  return [view, day, CALENDARS]
}

function isDay(text: string | undefined): text is string {
  return text !== undefined && /^\d{4}-\d{2}-\d{2}$/.test(text)
}
